/**
 * Shared form UX: loading state, disable submit while processing, message a11y.
 * Works with Contact Form 7 and native forms marked with [data-imucet-form].
 */
(() => {
	const getSubmitButtons = (form) =>
		Array.from(form.querySelectorAll('button[type="submit"], input[type="submit"], .wpcf7-submit'));

	const setFormSubmitting = (form, isSubmitting) => {
		if (!(form instanceof HTMLFormElement)) {
			return;
		}

		form.classList.toggle("imucet-form--loading", isSubmitting);
		form.setAttribute("aria-busy", isSubmitting ? "true" : "false");

		getSubmitButtons(form).forEach((btn) => {
			btn.disabled = isSubmitting;
			btn.setAttribute("aria-disabled", isSubmitting ? "true" : "false");
			btn.classList.toggle("btn--loading", isSubmitting);

			if (btn instanceof HTMLInputElement) {
				if (isSubmitting && !btn.dataset.imucetLabel) {
					btn.dataset.imucetLabel = btn.value;
					btn.value = "Sending…";
				} else if (!isSubmitting && btn.dataset.imucetLabel) {
					btn.value = btn.dataset.imucetLabel;
					delete btn.dataset.imucetLabel;
				}
			}
		});
	};

	const enhanceFormControls = (form) => {
		form.classList.add("imucet-form");

		form.querySelectorAll('input:not([type="submit"]):not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea, select').forEach((el) => {
			el.classList.add("imucet-form__control");
		});

		getSubmitButtons(form).forEach((btn) => {
			btn.classList.add("btn", "btn--gold", "btn--block");
		});

		const response = form.querySelector(".wpcf7-response-output");
		if (response) {
			response.classList.add("imucet-form__message");
			if (!response.hasAttribute("role")) {
				response.setAttribute("role", "status");
			}
			response.setAttribute("aria-live", "polite");
		}
	};

	const enhanceAllForms = () => {
		document.querySelectorAll(".wpcf7 form, form[data-imucet-form]").forEach((form) => {
			if (form instanceof HTMLFormElement) {
				enhanceFormControls(form);
			}
		});
	};

	const getFormFromEvent = (event) => {
		const unitTag = event?.detail?.unitTag;
		if (typeof unitTag === "string" && unitTag !== "") {
			const root =
				document.getElementById(unitTag) ||
				document.querySelector(`.${unitTag.replace(/([^\w-])/g, "\\$1")}`);
			const detailForm = root?.querySelector?.("form");
			if (detailForm instanceof HTMLFormElement) {
				return detailForm;
			}
		}

		const target = event?.target;
		if (target instanceof HTMLFormElement) {
			return target;
		}
		if (target instanceof Element) {
			const closest = target.closest("form");
			if (closest instanceof HTMLFormElement) {
				return closest;
			}
		}

		return null;
	};

	document.addEventListener(
		"submit",
		(event) => {
			const form = event.target;
			if (!(form instanceof HTMLFormElement)) {
				return;
			}

			if (!form.classList.contains("wpcf7-form") && !form.hasAttribute("data-imucet-form")) {
				return;
			}

			if (form.getAttribute("aria-busy") === "true") {
				event.preventDefault();
				event.stopPropagation();
				return;
			}

			setFormSubmitting(form, true);
		},
		true
	);

	document.addEventListener("wpcf7beforesubmit", (event) => {
		const form = getFormFromEvent(event);
		if (form) {
			setFormSubmitting(form, true);
		}
	});

	document.addEventListener("wpcf7invalid", (event) => {
		const form = getFormFromEvent(event);
		if (!form) {
			return;
		}

		setFormSubmitting(form, false);
		form.querySelectorAll(".wpcf7-not-valid").forEach((el) => {
			el.setAttribute("aria-invalid", "true");
		});

		const firstInvalid = form.querySelector(".wpcf7-not-valid, [aria-invalid='true']");
		if (firstInvalid instanceof HTMLElement) {
			firstInvalid.focus();
		}
	});

	document.addEventListener("wpcf7spam", (event) => {
		const form = getFormFromEvent(event);
		if (form) {
			setFormSubmitting(form, false);
		}
	});

	document.addEventListener("wpcf7mailfailed", (event) => {
		const form = getFormFromEvent(event);
		if (form) {
			setFormSubmitting(form, false);
		}
	});

	document.addEventListener("wpcf7mailsent", (event) => {
		const form = getFormFromEvent(event);
		if (!form) {
			return;
		}

		setFormSubmitting(form, false);
		form.querySelectorAll("[aria-invalid='true']").forEach((el) => {
			el.removeAttribute("aria-invalid");
		});

		const response = form.querySelector(".wpcf7-response-output");
		if (response instanceof HTMLElement) {
			response.focus?.();
		}
	});

	document.addEventListener("wpcf7submit", (event) => {
		const form = getFormFromEvent(event);
		if (!form) {
			return;
		}

		const status = event?.detail?.status;
		if (status && status !== "mail_sent") {
			setFormSubmitting(form, false);
		}
	});

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", enhanceAllForms);
	} else {
		enhanceAllForms();
	}

	document.addEventListener("wpcf7reset", (event) => {
		const form = getFormFromEvent(event);
		if (form) {
			setFormSubmitting(form, false);
			enhanceFormControls(form);
		}
	});
})();

/**
 * Safe GA event clicks via data attributes (no inline onclick).
 */
(() => {
	document.addEventListener("click", (event) => {
		const target = event.target instanceof Element ? event.target : null;
		const link = target?.closest?.("[data-ga-event]");
		if (!(link instanceof HTMLElement)) {
			return;
		}

		const eventName = link.getAttribute("data-ga-event");
		if (!eventName) {
			return;
		}

		const gtagFn = window.gtag;
		if (typeof gtagFn !== "function") {
			return;
		}

		gtagFn("event", eventName, {
			event_category: "IMUCET",
			event_label: link.getAttribute("data-ga-label") || eventName,
			link_url: link instanceof HTMLAnchorElement ? link.href : "",
			value: 1,
		});
	});
})();

(() => {
	const toggleBtn = document.querySelector("[data-mobile-menu-toggle]");
	const menu = document.getElementById("site-mobile-menu");
	const siteNav = document.querySelector(".site-nav");

	const setMobileMenuExpanded = (isExpanded) => {
		if (!toggleBtn || !menu) {
			return;
		}

		toggleBtn.setAttribute("aria-expanded", isExpanded ? "true" : "false");
		toggleBtn.setAttribute("aria-label", isExpanded ? "Close menu" : "Open menu");
		menu.hidden = !isExpanded;
	};

	setMobileMenuExpanded(false);

	if (toggleBtn && menu) {
		toggleBtn.addEventListener("click", () => {
			const isExpanded = toggleBtn.getAttribute("aria-expanded") === "true";
			setMobileMenuExpanded(!isExpanded);
		});
	}

	/**
	 * Same-document #section links: smooth scroll (respect reduced motion), optional mobile menu close.
	 * Skips modified clicks, middle-click, download links, and target=_blank.
	 */
	const normalizePath = (pathname) => {
		const p = pathname.replace(/\/$/, "");
		return p === "" ? "/" : p;
	};

	const scrollToHashTarget = (targetEl) => {
		const headerEl = document.querySelector(".site-header");
		const headerOffset = headerEl ? headerEl.getBoundingClientRect().height : 0;
		const extraGap = 12;
		const y =
			targetEl.getBoundingClientRect().top + window.scrollY - headerOffset - extraGap;
		const top = Math.max(0, y);
		const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		window.scrollTo({
			top,
			behavior: prefersReduced ? "auto" : "smooth",
		});
	};

	if (siteNav) {
		siteNav.addEventListener("click", (e) => {
			if (e.defaultPrevented || e.button !== 0) {
				return;
			}

			const link = e.target.closest("a[href]");
			if (!link || !siteNav.contains(link)) {
				return;
			}

			if (link.target === "_blank" || link.hasAttribute("download")) {
				return;
			}

			if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
				return;
			}

			let url;
			try {
				url = new URL(link.href, window.location.href);
			} catch {
				return;
			}

			if (url.origin !== window.location.origin) {
				return;
			}

			if (normalizePath(url.pathname) !== normalizePath(window.location.pathname)) {
				return;
			}

			const hash = url.hash;
			if (!hash || hash === "#") {
				return;
			}

			const id = decodeURIComponent(hash.slice(1));
			if (!id) {
				return;
			}

			const targetEl = document.getElementById(id);
			if (!targetEl) {
				return;
			}

			e.preventDefault();

			const fromMobileMenu = Boolean(menu && menu.contains(link));
			if (fromMobileMenu) {
				setMobileMenuExpanded(false);
				toggleBtn?.focus?.();
			}

			scrollToHashTarget(targetEl);

			try {
				history.pushState(null, "", hash);
			} catch {
				/* ignore history errors in restrictive environments */
			}
		});
	}

	const getModalEls = (targetId) => {
		const modal = document.getElementById(targetId);
		if (!modal) {
			return null;
		}

		const dialog = modal.querySelector("[data-modal-dialog]");
		const closeBtn = modal.querySelector("[data-modal-close]");
		return { modal, dialog, closeBtn };
	};

	const openModal = (targetId) => {
		const els = getModalEls(targetId);
		if (!els) {
			return;
		}

		els.modal.hidden = false;
		document.documentElement.style.overflow = "hidden";
		els.closeBtn?.focus?.();
	};

	const closeModal = (targetId) => {
		const els = getModalEls(targetId);
		if (!els) {
			return;
		}

		els.modal.hidden = true;
		document.documentElement.style.overflow = "";
	};

	document.addEventListener("click", (e) => {
		const clickedEl = e.target instanceof Element ? e.target : e.target?.parentElement;
		if (!clickedEl) {
			return;
		}

		const openBtn = clickedEl.closest("[data-modal-open]");
		if (openBtn) {
			const targetId = openBtn.getAttribute("data-modal-open");
			if (targetId) {
				openModal(targetId);
			}
			return;
		}

		const closeBtn = clickedEl.closest("[data-modal-close]");
		if (closeBtn) {
			const targetId = closeBtn.getAttribute("data-modal-close");
			if (targetId) {
				closeModal(targetId);
			}
			return;
		}

		const modalRoot = clickedEl.closest("[data-modal-root]");
		if (modalRoot && clickedEl === modalRoot) {
			const targetId = modalRoot.getAttribute("id");
			if (targetId) {
				closeModal(targetId);
			}
		}
	});

	document.addEventListener("keydown", (e) => {
		if (e.key !== "Escape") {
			return;
		}

		setMobileMenuExpanded(false);

		const openModalEl = document.querySelector("[data-modal-root]:not([hidden])");
		if (openModalEl?.id) {
			closeModal(openModalEl.id);
		}
	});
})();

/**
 * Rank Math FAQ: one-at-a-time accordion (click / Enter / Space on question).
 * Adds class `rank-math-faq--accordion` and toggles `[hidden]` on answers — no HTML template changes.
 */
(() => {
	const root = document.querySelector("#rank-math-faq");
	if (!root || !root.querySelector(".rank-math-list")) {
		return;
	}

	const items = Array.from(root.querySelectorAll(".rank-math-list-item"));
	if (items.length === 0) {
		return;
	}

	root.classList.add("rank-math-faq--accordion");

	const closeItem = (listItem) => {
		const question = listItem.querySelector(".rank-math-question");
		const answer = listItem.querySelector(".rank-math-answer");
		if (!question || !answer) {
			return;
		}
		question.setAttribute("aria-expanded", "false");
		answer.setAttribute("hidden", "hidden");
		listItem.classList.remove("rank-math-list-item--open");
	};

	const openItem = (listItem) => {
		const question = listItem.querySelector(".rank-math-question");
		const answer = listItem.querySelector(".rank-math-answer");
		if (!question || !answer) {
			return;
		}
		question.setAttribute("aria-expanded", "true");
		answer.removeAttribute("hidden");
		listItem.classList.add("rank-math-list-item--open");
	};

	const closeAll = () => {
		items.forEach(closeItem);
	};

	items.forEach((listItem) => {
		const question = listItem.querySelector(".rank-math-question");
		const answer = listItem.querySelector(".rank-math-answer");
		if (!question || !answer || !(question instanceof HTMLElement)) {
			return;
		}

		question.setAttribute("role", "button");
		question.setAttribute("tabindex", "0");
		question.setAttribute("aria-expanded", "false");
		answer.setAttribute("hidden", "hidden");

		const toggle = () => {
			const isOpen = question.getAttribute("aria-expanded") === "true";
			if (isOpen) {
				closeItem(listItem);
				return;
			}
			closeAll();
			openItem(listItem);
		};

		question.addEventListener("click", (event) => {
			if (event.target instanceof HTMLAnchorElement) {
				return;
			}
			event.preventDefault();
			toggle();
		});

		question.addEventListener("keydown", (event) => {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				toggle();
			}
		});
	});
})();
